import Explainer from './Explainer.jsx';
import { Box, Wire, Packet, Cub, Title, ExplainerDefs } from './parts.jsx';

/**
 * Junior Module 1, lesson 1: How the Internet Works.
 *
 * The same player as the Ethical Hacker explainers, pitched at 7-12:
 * shorter scenes, shorter sentences, one idea each, and a picture
 * doing most of the work.
 *
 * The spine is a single message going from one child to another, and
 * the promise at the start is that by the end they will be able to
 * explain it to a grown-up. The last scene keeps that promise by
 * asking them to.
 *
 * Deliberately no jargon without a picture attached: "server" arrives
 * as a computer that never sleeps, "packet" as a message cut into
 * pieces, and neither word turns up before the thing it names.
 */

function SceneHook({ beat }) {
  return (
    <g>
      <Title x="10" y="12" text="You send a message" sub="how does it get there?" />
      <Cub x="26" y="46" scale="1.3" talking={beat >= 1} />
      <text className="xp-note" x="26" y="70">you</text>

      {beat >= 1 && (
        <g className="xp-in">
          <rect className="xp-frame" x="46" y="34" width="44" height="16" rx="3" />
          <text className="xp-big" x="68" y="45">hello!</text>
        </g>
      )}
      {beat >= 2 && (
        <g className="xp-in">
          <Cub x="134" y="46" scale="1.3" />
          <text className="xp-note" x="134" y="70">your friend</text>
          <Packet from={[92, 42]} to={[122, 42]} tone="mint" />
        </g>
      )}
      {beat >= 3 && (
        <text className="xp-big xp-in" x="80" y="84">it gets there in less than a second</text>
      )}
    </g>
  );
}

function SceneWires({ beat }) {
  return (
    <g>
      <Title x="10" y="12" text="It is not flying through the air" sub="mostly, it is going down a wire" />
      <Box x="10" y="34" w="30" h="18" label="your house" tone="sky" lit />
      {beat >= 1 && (
        <g className="xp-in">
          <Wire from={[40, 43]} to={[64, 43]} />
          <Box x="64" y="34" w="32" h="18" label="under the road" tone="grape" lit />
        </g>
      )}
      {beat >= 2 && (
        <g className="xp-in">
          <Wire from={[96, 43]} to={[118, 43]} />
          <Box x="118" y="34" w="34" h="18" label="under the sea" tone="grape" lit />
        </g>
      )}
      {beat >= 3 && (
        <g className="xp-in">
          <text className="xp-note" x="80" y="64">real cables, on the bottom of the ocean</text>
          <text className="xp-big" x="80" y="76">thinner than your arm, and they carry the whole internet</text>
        </g>
      )}
      {beat >= 4 && <Cub x="24" y="80" scale="0.7" talking />}
    </g>
  );
}

function ScenePieces({ beat }) {
  return (
    <g>
      <Title x="10" y="12" text="Your message gets cut up" sub="into little pieces" />
      <rect className="xp-frame" x="16" y="26" width="46" height="16" rx="3" />
      <text className="xp-big" x="39" y="37">a photo of your dog</text>

      {beat >= 1 && [0, 1, 2, 3].map((i) => (
        <g key={i} className="xp-in">
          <rect className="xp-byte" x={74 + i * 14} y="26" width="12" height="14" rx="2" />
          <text className="xp-mono is-small" x={80 + i * 14} y="35">{i + 1}</text>
        </g>
      ))}
      {beat >= 1 && <text className="xp-note xp-in" x="101" y="48">each piece is called a packet</text>}

      {beat >= 2 && (
        <g className="xp-in">
          <text className="xp-note" x="80" y="62">they all travel on their own…</text>
          <Packet from={[30, 72]} to={[130, 72]} tone="mint" />
          <Packet from={[30, 78]} to={[130, 78]} delay={0.4} tone="sky" />
          <Packet from={[30, 84]} to={[130, 84]} delay={0.8} tone="mint" />
        </g>
      )}
      {beat >= 3 && (
        <text className="xp-big xp-in" x="80" y="94">…and get stuck back together at the other end</text>
      )}
    </g>
  );
}

function SceneAddress({ beat }) {
  return (
    <g>
      <Title x="10" y="12" text="Every packet has an address" sub="like writing it on an envelope" />
      <rect className="xp-frame" x="38" y="26" width="84" height="30" rx="3" />
      <text className="xp-note is-left" x="44" y="36">TO:</text>
      {beat >= 1 && <text className="xp-mono xp-in" x="80" y="45">203.0.113.9</text>}
      {beat >= 2 && (
        <text className="xp-note xp-in" x="80" y="66">
          every computer on the internet has a number like that
        </text>
      )}
      {beat >= 3 && (
        <g className="xp-in">
          <text className="xp-note" x="80" y="78">but you type words, not numbers</text>
          <text className="xp-big" x="80" y="88">something turns the words into the number for you</text>
        </g>
      )}
    </g>
  );
}

function SceneServer({ beat }) {
  return (
    <g>
      <Title x="10" y="12" text="Computers that never sleep" sub="these are called servers" />
      {[0, 1, 2].map((i) => (
        <g key={i} className={i <= beat ? 'xp-in' : 'xp-off'}>
          <rect className="xp-frame" x={30 + i * 36} y="26" width="28" height="34" rx="2" />
          {[0, 1, 2, 3].map((r) => (
            <rect key={r} className="xp-byte" x={33 + i * 36} y={29 + r * 8} width="22" height="6" rx="1" />
          ))}
        </g>
      ))}
      {beat >= 2 && (
        <g className="xp-in">
          <text className="xp-note" x="80" y="70">they keep your photos, messages and games</text>
          <text className="xp-big" x="80" y="82">and they are switched on all day and all night</text>
        </g>
      )}
      {beat >= 3 && <Cub x="136" y="78" scale="0.8" talking />}
    </g>
  );
}

function SceneJourney({ beat }) {
  const stops = [
    { x: 4, label: 'you' },
    { x: 34, label: 'your router' },
    { x: 66, label: 'lots of wires' },
    { x: 98, label: 'a server' },
    { x: 130, label: 'your friend' },
  ];
  return (
    <g>
      <Title x="10" y="12" text="The whole trip" sub="and it takes less than a second" />
      {stops.map((s, i) => (
        <g key={s.label} className={i <= beat ? 'xp-in' : 'xp-off'}>
          <Box x={s.x} y="34" w="26" h="20" label={s.label} tone={i === 3 ? 'sun' : 'grape'} lit={i === beat} />
          {i > 0 && <Wire from={[stops[i - 1].x + 26, 44]} to={[s.x, 44]} arrow />}
        </g>
      ))}
      {beat >= 4 && (
        <g className="xp-in">
          <text className="xp-big" x="80" y="70">hello! 🎉</text>
          <text className="xp-note" x="80" y="82">and the whole thing happens again when they reply</text>
        </g>
      )}
    </g>
  );
}

function SceneSafe({ beat }) {
  return (
    <g>
      <Title x="10" y="12" text="One important thing" sub="the wires are not yours" />
      {beat >= 0 && (
        <g>
          <Box x="14" y="30" w="44" h="20" label="your message" tone="sky" lit />
          <Wire from={[58, 40]} to={[100, 40]} arrow />
          <text className="xp-note" x="79" y="34">goes past lots of other computers</text>
        </g>
      )}
      {beat >= 1 && (
        <g className="xp-in">
          <Box x="102" y="30" w="44" h="20" label="your friend" tone="grape" lit />
        </g>
      )}
      {beat >= 2 && (
        <g className="xp-in">
          <rect className="xp-frame is-good" x="20" y="58" width="120" height="16" rx="2" />
          <text className="xp-big" x="80" y="69">🔒 the padlock means it is scrambled on the way</text>
        </g>
      )}
      {beat >= 3 && (
        <text className="xp-note xp-in" x="80" y="84">
          so the other computers carry it, but they cannot read it
        </text>
      )}
    </g>
  );
}

function SceneRecap({ beat }) {
  const rows = [
    'The internet is wires, joining computers together',
    'Your message gets cut into little packets',
    'Each one has an address, like an envelope',
    'Servers are computers that never switch off',
    'The padlock means nobody can read it on the way',
  ];
  return (
    <g>
      <Title x="10" y="12" text="Now you can explain it" sub="to a grown-up, if you like" />
      {rows.slice(0, beat + 1).map((r, i) => (
        <g key={r} className="xp-in">
          <circle className="xp-tick" cx="24" cy={28 + i * 11} r="3.2" />
          <text className="xp-tick-mark" x="24" y={29.4 + i * 11}>✓</text>
          <text className="xp-row" x="32" y={29.8 + i * 11}>{r}</text>
        </g>
      ))}
      {beat >= 5 && <Cub x="136" y="66" scale="1" talking />}
    </g>
  );
}

const SCENES = [
  {
    id: 'hook', title: 'You send a message', mood: 'intro', Art: SceneHook,
    beats: [
      { say: 'You type hello to your friend and press send.', cue: 'click' },
      { say: 'Your friend might be next door. Or they might be on the other side of the world.', cue: 'dataFlow' },
      { say: 'And it gets there in less than a second.', cue: 'reveal' },
      { say: 'How? Let us follow it and find out.', cue: 'confirmBeep' },
    ],
  },
  {
    id: 'wires', title: 'Wires everywhere', mood: 'calm', Art: SceneWires,
    beats: [
      { say: 'First, a surprise. Your message is not flying through the sky.', cue: 'click' },
      { say: 'It goes out of your house, into a wire under the road.', cue: 'dataFlow' },
      { say: 'And if your friend is far away, it goes into a cable lying on the bottom of the sea.', cue: 'dataFlow' },
      { say: 'Those cables are real. They are thinner than your arm, and they cross whole oceans.', cue: 'reveal' },
      { say: 'Almost everything you do online goes down one of them.', cue: 'confirmBeep' },
    ],
  },
  {
    id: 'pieces', title: 'Little pieces', mood: 'calm', Art: ScenePieces,
    beats: [
      { say: 'Now something odd. Your message does not travel in one piece.', cue: 'click' },
      { say: 'It gets chopped into lots of tiny bits. Each bit is called a packet.', cue: 'dataFlow' },
      { say: 'They all set off on their own, and they do not even have to go the same way.', cue: 'dataFlow' },
      { say: 'Then at the other end, they get stuck back together in the right order. Like a jigsaw that puts itself together.', cue: 'reveal' },
    ],
  },
  {
    id: 'address', title: 'Like an envelope', mood: 'calm', Art: SceneAddress,
    beats: [
      { say: 'How does each packet know where to go? It has an address on it, just like a letter.', cue: 'click' },
      { say: 'But the address is not a street. It is a number.', cue: 'dataFlow' },
      { say: 'Every computer on the internet has one, so packets can find it.', cue: 'confirmBeep' },
      { say: 'You never type those numbers though. You type words — and something clever turns the words into the number for you.', cue: 'reveal' },
    ],
  },
  {
    id: 'server', title: 'Computers that never sleep', mood: 'calm', Art: SceneServer,
    beats: [
      { say: 'On the way, your message usually stops at a special computer called a server.', cue: 'click' },
      { say: 'Servers are just computers. But they are switched on all day and all night, and they never go to sleep.', cue: 'dataFlow' },
      { say: 'They keep your photos, your messages and your games, so you can get them from any device.', cue: 'reveal' },
      { say: 'That is why your chat is still there when you get a new phone. It was never really on the old one.', cue: 'confirmBeep' },
    ],
  },
  {
    id: 'journey', title: 'The whole trip', mood: 'resolve', Art: SceneJourney,
    beats: [
      { say: 'So here is the whole trip. It starts with you.', cue: 'click' },
      { say: 'Out to the little box in your house — the router.', cue: 'dataFlow' },
      { say: 'Then through lots and lots of wires.', cue: 'dataFlow' },
      { say: 'Past a server, which keeps a copy.', cue: 'dataFlow' },
      { say: 'And finally, ping — it arrives on your friend’s screen. All of that, faster than you can blink.', cue: 'fanfare' },
    ],
  },
  {
    id: 'safe', title: 'The important bit', mood: 'tense', Art: SceneSafe,
    beats: [
      { say: 'One last thing, and it is the important one.', cue: 'warnLow' },
      { say: 'Your message went past lots of other computers on the way. None of them belong to you.', cue: 'click' },
      { say: 'So messages get scrambled before they set off. That is what the little padlock means.', cue: 'reveal' },
      { say: 'The other computers still carry your message — they just cannot read it. Clever, isn’t it?', cue: 'confirmBeep' },
    ],
  },
  {
    id: 'recap', title: 'You know how it works', mood: 'resolve', Art: SceneRecap,
    beats: [
      { say: 'Let us remember it all.', cue: 'click' },
      { say: 'The internet is wires, joining computers together.', cue: 'confirmBeep' },
      { say: 'Your message gets cut into little packets.', cue: 'confirmBeep' },
      { say: 'Each one has an address on it, like an envelope.', cue: 'confirmBeep' },
      { say: 'Servers are computers that never switch off.', cue: 'confirmBeep' },
      { say: 'And the padlock means nobody can read your message on the way. Go and explain that to a grown-up — most of them do not know it.', cue: 'fanfare' },
    ],
  },
];

export default function JuniorInternet() {
  return (
    <Explainer
      id="junior-internet"
      title="How does the internet work?"
      subtitle="Animated explainer · Module 1, lesson 1"
      backTo="/learn/lesson/l1-how-the-internet-works"
      scenes={SCENES}
      Defs={ExplainerDefs}
      closing={{
        title: 'Wires, packets and envelopes.',
        text:
          'Your message goes down real cables, cut into little pieces, each with an address on it — and the padlock keeps it secret the whole way. Now try the practical and send one yourself.',
      }}
    />
  );
}
